import {
  Box, Typography, TextField, InputAdornment, Checkbox, Card, CardContent, Grid, useMediaQuery, Button, Tooltip, Dialog, Chip, Stack, IconButton as MuiIconButton,
  IconButton, Badge
} from "@mui/material";
import {
  Search as SearchIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Autorenew as AutorenewIcon,
  DoneAll as DoneAllIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  PhotoCamera as PhotoCameraIcon,
  AccessTime as AccessTimeIcon,
  AlarmOn as AlarmOnIcon,
  AlarmOff as AlarmOffIcon,
  PriorityHigh as PriorityHighIcon,
  WarningAmber as WarningIcon,
  LowPriority as LowPriorityIcon,
  Pause as PauseIcon
} from "@mui/icons-material";
import HistoryIcon from '@mui/icons-material/History';
import { useTheme, styled } from "@mui/material/styles";
import { toast } from "react-toastify";
import ToolTipTktCont from "../../../../GlobalFunction/ToolTipTktCont";
import { useLayoutEffect, useRef, useState } from "react";
import TicketDetailsPopup from "../RaiseServTkt/TktDetailsPopup";

const StyledCard = styled(Card)(({ theme }) => ({
  flexGrow: 1,
  borderRadius: "16px",
  background: "#fff",
  border: `2px solid ${theme.palette.grey[300]}`,
  transition: "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease, opacity 0.3s ease",
  "&:hover": {
    transform: "translateY(1px)",
    boxShadow: theme.shadows[2],
    borderColor: theme.palette.primary.light,
  },
  "&:active": {
    transform: "scale(0.98)",
  },
  position: "relative",
  overflow: "hidden",
  "&:before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "radial-gradient(circle, rgba(0,0,0,0.1) 0%, transparent 70%)",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  "&:active:before": {
    opacity: 1,
  },
  "&.selected": {
    boxShadow: theme.shadows[8],
  },
}));

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getStatusIcon = (status, theme) => {
  switch (status) {
    case "O":
      return <HourglassEmptyIcon sx={{ color: theme.palette.warning.dark, fontSize: "1.2rem" }} />;
    case "I":
      return <AutorenewIcon sx={{ color: "#D32F2F", fontSize: "1.2rem" }} />;
    case "H":
      return <PauseIcon sx={{ color: "#9c27b0", fontSize: "1.2rem" }} />;
    case "R":
      return <DoneAllIcon sx={{ color: theme.palette.success.dark, fontSize: "1.2rem" }} />;
    case "C":
      return <CancelIcon sx={{ color: theme.palette.error.dark, fontSize: "1.2rem" }} />;
    default:
      return null;
  }
};

const fixImageUrl = (url) => {
  if (!url) return null;
  return url.replace(/^http:\/([^/])/, "http://$1");
};

const FilterChip = styled(Chip)(({ theme, filtertype, selected }) => {
  const colors = {
    high: { main: "#ff7961", light: "#ff9a7a", dark: "#f4511e" },
    medium: { main: "#ffb74d", light: "#ffcc80", dark: "#f57c00" },
    low: { main: "#64b5f6", light: "#90caf9", dark: "#1e88e5" },
    ontime: { main: "#81c784", light: "#a5d6a7", dark: "#43a047" },
    delayed: { main: "#e57373", light: "#ffcdd2", dark: "#e53935" },
  };

  const color = colors[filtertype] || colors.low;

  return {
    height: 36,
    fontWeight: 700,
    fontSize: "0.92rem",
    borderRadius: "24px",
    minWidth: 80,
    padding: "0 4px",
    cursor: "pointer",
    transition: "all 0.25s ease",
    border: "none",

    // Default state = LIGHT color
    backgroundColor: selected ? color.dark : color.light,
    color: selected ? "white" : color.dark,
    border: selected ? `2px solid ${theme.palette.primary.main}` : "none",
    boxShadow: selected
      ? `0 8px 25px ${color.dark}60`
      : "0 3px 10px rgba(0,0,0,0.08)",

    "& .MuiChip-icon": {
      color: selected ? "white" : color.dark,
    },

    // ON HOVER → ALWAYS USE DARK COLOR
    "&:hover": {
      backgroundColor: `${color.dark} !important`,
      color: "white",
      transform: "translateY(-2px)",
      boxShadow: `0 10px 20px ${color.dark}50 !important`,
      "& .MuiChip-icon": {
        color: "white !important",
      },
    },

    "&:active": {
      transform: "scale(0.98)",
    },
  };
});

const TaskStatusCards = ({
  rows,
  searchTerm,
  setSearchTerm,
  selectedTicket,
  handleRowClick,
  getStatusChip,
  selectedRows,
  setSelectedRows,
  selectedCategoryId,
  setSelectedCategoryId,
  handleUpdate,
  onOpenHistory,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ZOOM STATE (Same as TaskAcceptanceTable)
  const [zoomedTicketId, setZoomedTicketId] = useState(null);
  const [zoomedImageUrl, setZoomedImageUrl] = useState(null);
  // Filter States
  const [priorityFilter, setPriorityFilter] = useState("");
  const [slaFilter, setSlaFilter] = useState("");

  // ADD THESE TWO:
  const [detailPopupOpen, setDetailPopupOpen] = useState(false);
  const [selectedTicketDetail, setSelectedTicketDetail] = useState(null);

  const handleImageClick = (e, ticketId, imageUrl) => {
    e.stopPropagation();
    if (imageUrl) {
      setZoomedTicketId((prev) => (prev === ticketId ? null : ticketId));
      setZoomedImageUrl((prev) => (prev === imageUrl ? null : imageUrl));
    }
  };

  // CLICK OUTSIDE TO CLOSE (Same as TaskAcceptanceTable)
  const handleOutsideClick = (e) => {
    if (zoomedTicketId && e.target.id === "zoom-overlay") {
      setZoomedTicketId(null);
      setZoomedImageUrl(null);
    }
  };
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high": return "#FF4D4F";
      case "medium": return "#FA8C16";
      case "low": return "#52C41A";
      default: return "#8C8C8C";
    }
  };

  const getDelaySeverityColor = (delay) => {
    if (!delay || delay.includes("Min") || delay === "0.00 Min") return "#52C41A";
    const days = parseInt(delay);
    if (days >= 20) return "#FF4D4F";
    if (days >= 10) return "#FA8C16";
    if (days >= 1) return "#FFB302";
    return "#52C41A";
  };

  // FILTER ROWS
  // const filteredRows = Array.isArray(rows)
  //   ? rows.filter((row) => {
  //     const search = searchTerm.toLowerCase();
  //     return (
  //       formatDate(row.TktDate).toLowerCase().includes(search) ||
  //       formatTime(row.TktDate).toLowerCase().includes(search) ||
  //       (row.TicketNo && row.TicketNo.toLowerCase().includes(search)) ||
  //       (row.ServiceName && row.ServiceName.toLowerCase().includes(search)) ||
  //       (row.CategoryName && row.CategoryName.toLowerCase().includes(search)) ||
  //       (row.TicketTypeName && row.TicketTypeName.toLowerCase().includes(search)) ||
  //       (row.TicketServName && row.TicketServName.toLowerCase().includes(search)) ||
  //       (row.TicketTagName && row.TicketTagName.toLowerCase().includes(search)) ||
  //       (row.PropName && row.PropName.toLowerCase().includes(search)) ||
  //       (row.ServiceProvider && row.ServiceProvider.toLowerCase().includes(search)) ||
  //       getStatusChip(row.TicketSt).props.label.toLowerCase().includes(search)
  //     );
  //   })
  //   : [];
  // FINAL WORKING FILTER LOGIC (Same as MultipleCards)
  const filteredRows = Array.isArray(rows)
    ? rows.filter((row) => {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        formatDate(row.TktDate).toLowerCase().includes(search) ||
        formatTime(row.TktDate).toLowerCase().includes(search) ||
        (row.TicketNo?.toLowerCase().includes(search)) ||
        (row.ServiceName?.toLowerCase().includes(search)) ||
        (row.CategoryName?.toLowerCase().includes(search)) ||
        (row.PropName?.toLowerCase().includes(search)) ||
        (row.ServiceProvider?.toLowerCase().includes(search)) ||
        getStatusChip(row.TicketSt).props.label.toLowerCase().includes(search);

      // Priority Filter
      const matchesPriority = !priorityFilter ||
        (priorityFilter === "High" && /high/i.test(row.TicketServName)) ||
        (priorityFilter === "Medium" && /medium/i.test(row.TicketServName)) ||
        (priorityFilter === "Low" && /low/i.test(row.TicketServName));

      // SLA Filter - BULLETPROOF
      const isOnTime = () => {
        if (!row.DelayTime || row.DelayTime === "On Time" || row.DelayTime.trim() === "") return true;
        const d = row.DelayTime.trim().toLowerCase();
        if (d.includes("day") || d.includes("hr") || d.includes("hour")) return false;
        if (d.includes("min") || d.includes("minute")) return true;
        if (d.includes("0")) return true;
        return true;
      };

      const matchesSLA = !slaFilter ||
        (slaFilter === "OnTime" && isOnTime()) ||
        (slaFilter === "Delayed" && !isOnTime());

      return matchesSearch && matchesPriority && matchesSLA;
    })
    : [];

  // DYNAMIC HEIGHT
  const containerRef = useRef(null);
  const [dynamicHeight, setDynamicHeight] = useState(300);
  const MAX_HEIGHT = 580;
  const REDUCE_BY = 80;

  const updateHeight = () => {
    const el = containerRef.current;
    if (!el) return;

    let parent = el.parentElement;
    while (parent && window.getComputedStyle(parent).overflow !== "auto" && parent !== document.body) {
      parent = parent.parentElement;
    }

    if (!parent || parent === document.body) {
      const headerHeight = 120;
      const calc = window.innerHeight - headerHeight - REDUCE_BY;
      setDynamicHeight(Math.min(MAX_HEIGHT, Math.max(200, calc)));
      return;
    }

    requestAnimationFrame(() => {
      const parentRect = parent.getBoundingClientRect();
      const selfRect = el.getBoundingClientRect();
      const usedAbove = selfRect.top - parentRect.top;
      const available = parentRect.height - usedAbove - 40;
      const final = Math.max(200, available - REDUCE_BY);
      setDynamicHeight(Math.min(MAX_HEIGHT, final));
    });
  };

  useLayoutEffect(() => {
    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    const el = containerRef.current;
    if (el) {
      let target = el.parentElement;
      while (target && window.getComputedStyle(target).overflow !== "auto" && target !== document.body) {
        target = target.parentElement;
      }
      if (target && target !== document.body) ro.observe(target);
    }

    const handleResize = () => requestAnimationFrame(updateHeight);
    window.addEventListener("resize", handleResize);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, [filteredRows.length]);

  // SELECTION LOGIC
  const handleCheckboxChange = (ticketId, categoryId) => {
    setSelectedRows((prev) => {
      if (prev.includes(ticketId)) {
        const newSelectedRows = prev.filter((id) => id !== ticketId);
        if (newSelectedRows.length === 0) {
          setSelectedCategoryId(null);
        }
        return newSelectedRows;
      } else {
        if (prev.length === 0) {
          setSelectedCategoryId(categoryId);
        }
        return [...prev, ticketId];
      }
    });
  };

  // const handleCardClick = (row) => {
  //   if (selectedRows.length === 0) handleRowClick(row);
  // };
  const handleCardClick = (row) => {
    setSelectedTicketDetail(row);
    setDetailPopupOpen(true);
  };

  const RowItem = ({ label, value, useTooltip = true }) => (
    <Box sx={{ display: "flex", alignItems: "baseline", mb: 0.5 }}>
      <Typography
        component="span"
        variant="body2"
        sx={{
          fontWeight: 700,
          whiteSpace: "nowrap",
          mr: 0.5,
          fontSize: { xs: "0.99rem", sm: "0.99rem", md: "0.85rem" },
        }}
      >
        {label}:
      </Typography>

      {useTooltip ? (
        <ToolTipTktCont title={value}>{value || "N/A"}</ToolTipTktCont>
      ) : (
        <Typography
          component="span"
          variant="body2"
          sx={{
            fontSize: { xs: "0.99rem", sm: "0.99rem", md: "0.85rem" },
            wordBreak: "break-word",
          }}
        >
          {value || "N/A"}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ padding: 2, width: "100%" }}>

      {/* <Box sx={{ width: "100%", mb: 1 }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search Tickets"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            sx: {
              borderRadius: "12px",
              backgroundColor: theme.palette.background.paper,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.divider,
              },
              fontSize: "0.85rem",
              height: "40px",
            },
          }}
        />
      </Box> */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          alignItems: { xs: "stretch", md: "center" },
          mb: 1.5,
        }}
      >
        {/* Left: Search */}
        <Box
          sx={{
            flex: { xs: "1 1 100%", md: "1" },
            maxWidth: { xs: "100%", md: "50%" },
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Search Tickets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputBase-root": {
                height: 40,
                borderRadius: "16px",
                fontSize: "0.95rem",
                backgroundColor: "#f8fafc",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              },
            }}
          />
        </Box>

        {/* Right: Filter Chips */}
        <Stack
          direction={{ xs: "row", md: "row" }}
          spacing={0.5}
          flexWrap="wrap"
          justifyContent={{ xs: "center", md: "flex-end" }}
          sx={{
            gap: 0.2,
            width: { xs: "100%", md: "auto" },
            "& > *": {
              flex: { xs: "1 1 45%", md: "0 0 auto" },
              minWidth: { xs: "120px", md: "auto" },
            },
          }}
        >
          {/* Priority Filters */}
          <FilterChip
            icon={<PriorityHighIcon />}
            label="High Priority"
            filtertype="high"
            selected={priorityFilter === "High"}
            onClick={() => {
              setPriorityFilter("High");
              setSlaFilter("");
            }}
            sx={{
              "&&": {
                mb: { xs: 0.3, md: 0 },
              },
            }}
          />
          <FilterChip
            icon={<WarningIcon />}
            label="Medium Priority"
            filtertype="medium"
            selected={priorityFilter === "Medium"}
            onClick={() => {
              setPriorityFilter("Medium");
              setSlaFilter("");
            }}
            sx={{
              "&&": {
                mb: { xs: 0.3, md: 0 },
              },
            }}
          />
          <FilterChip
            icon={<LowPriorityIcon />}
            label="Low Priority"
            filtertype="low"
            selected={priorityFilter === "Low"}
            onClick={() => {
              setPriorityFilter("Low");
              setSlaFilter("");
            }}
            sx={{
              "&&": {
                mb: { xs: 0.3, md: 0 },
              },
            }}
          />

          {/* SLA Filters */}
          <FilterChip
            icon={<AlarmOnIcon />}
            label="On-Time"
            filtertype="ontime"
            selected={slaFilter === "OnTime"}
            onClick={() => {
              setSlaFilter("OnTime");
              setPriorityFilter("");
            }}
            sx={{
              "&&": {
                mb: { xs: 0.3, md: 0 },
              },
            }}
            clickable
            variant={slaFilter === "OnTime" ? "filled" : "outlined"}
          />
          <FilterChip
            icon={<AlarmOffIcon />}
            label="Delayed"
            filtertype="delayed"
            selected={slaFilter === "Delayed"}
            onClick={() => setSlaFilter(slaFilter === "Delayed" ? "" : "Delayed")}
            clickable
            variant={slaFilter === "Delayed" ? "filled" : "outlined"}
          />

          {/* Clear All */}
          {(priorityFilter || slaFilter) && (
            <FilterChip
              label="Clear Filters"
              onClick={() => {
                setPriorityFilter("");
                setSlaFilter("");
              }}
              sx={{
                backgroundColor: "#ffebee",
                color: "#c62828",
                border: "2px solid #ffcdd2",
                fontWeight: 700,
                "&:hover": { backgroundColor: "#ffcdd2", color: "#b71c1c" },
              }}
            />
          )}
        </Stack>
      </Box>

      <Box ref={containerRef} sx={{ width: "100%" }}>
        {filteredRows.length > 0 ? (
          <Grid container spacing={2}
            sx={{
              minHeight: `${dynamicHeight}px`,
              display: "flex",
              alignItems: "stretch",
            }}
          >
            {filteredRows.map((row) => {
              const imageUrl = fixImageUrl(row.ImgName);
              const hasImage = !!imageUrl;

              return (
                <Grid item xs={12} sm={6} md={3} key={row.TicketId}
                  sx={{
                    display: "flex",
                    height: "100%",
                  }}
                >
                  <StyledCard
                    className={row.TicketId === selectedTicket?.TicketId ? "selected" : ""}
                    // onClick={() => row.TicketSt !== "O" && handleCardClick(row)}
                    onClick={() => handleCardClick(row)}
                    sx={{
                      // cursor: row.TicketSt === "O" ? "not-allowed" : "pointer",
                      cursor: "pointer",
                      opacity: row.TicketSt === "O" ? 1 : 1,
                      // pointerEvents: row.TicketSt === "O" ? "none" : "auto",
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardContent
                      sx={{
                        padding: "16px !important",
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* HEADER: Checkbox + Ticket No + Status + Camera */}
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-start"
                        mb={1}
                        gap={0}
                        flexWrap="nowrap"
                        sx={{ overflow: "hidden", whiteSpace: "nowrap", marginX: { xs: -0.5, sm: 0, md: -1 } }}
                      >
                        {/* CHECKBOX */}
                        <Tooltip
                          title={(row.TicketSt === "C" || row.TicketSt === "R") ? "Resolved and Closed tickets cannot be updated" : ""}
                          placement="top"
                          arrow
                        >
                          <span>
                            <Checkbox
                              checked={selectedRows.includes(row.TicketId)}
                              disabled={row.TicketSt === "C" || row.TicketSt === "R"}
                              onClick={(e) => e.stopPropagation()}
                              // onChange={() => row.TicketSt !== "O" && handleCheckboxChange(row.TicketId, row.TktCategoryId)}
                              onChange={() => handleCheckboxChange(row.TicketId, row.TktCategoryId)}
                              size="small"
                              sx={{
                                padding: "0px",
                                color: row.TicketSt === "O" ? "action.disabled" : "primary.main",
                                '&.Mui-disabled': { opacity: 0.5 },
                              }}
                            />
                          </span>
                        </Tooltip>

                        <Box display="flex" alignItems="center" gap={0.3} mr={1}>
                          {getStatusIcon(row.TicketSt, theme)}
                          <Typography
                            variant="subtitle1"
                            color="text.primary"
                            fontWeight="bold"
                            sx={{ fontSize: { xs: "1rem", sm: "1rem", md: "0.89rem" } }}
                          >
                            Tkt No. {row.TicketNo}
                          </Typography>

                          {/* STATUS CHIP + CAMERA */}
                          <Box display="flex" alignItems="center" gap={0}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: { xs: "1rem", sm: "0.55rem" },
                                overflow: "visible",
                                whiteSpace: "normal",
                              }}
                            >
                              {getStatusChip(row.TicketSt)}
                            </Typography>

                            {/* CAMERA ICON – Same behavior as TaskAcceptanceTable */}
                            {hasImage && (
                              <PhotoCameraIcon
                                onClick={(e) => handleImageClick(e, row.TicketId, imageUrl)}
                                sx={{
                                  fontSize: { xs: "1.5rem", sm: "1.5rem" },
                                  color: theme.palette.info.main,
                                  cursor: "pointer",
                                  ml: 0,
                                  transition: "transform 0.3s ease-in-out, color 0.3s ease-in-out",
                                  transform: zoomedTicketId === row.TicketId ? "scale(1)" : "scale(1)",
                                  "&:hover": {
                                    transform: "scale(1.1)",
                                    color: theme.palette.info.dark,
                                  },
                                }}
                              />
                            )}
                          </Box>
                          <Box display="flex" alignItems="center">
                            {/* <Badge badgeContent={row.TotalCount.length || 2} color="error"
                              sx={{
                                '.MuiBadge-badge': {
                                  fontSize: '0.6rem',
                                  height: '16px',
                                  minWidth: '16px'
                                }
                              }}
                            >
                            </Badge> */}
                            <Tooltip title="Check follow up history" arrow>
                              <IconButton sx={{ p: 0 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onOpenHistory(row.TicketId);
                                }}
                              >
                                <HistoryIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Box>

                      {/* ALL ROWS */}
                      <RowItem label="RaisedBy" value={row.Name} />
                      <RowItem
                        label="Date"
                        value={`${formatDate(row.TktDate)} | ${formatTime(row.TktDate)}`}
                        useTooltip={false}
                      />
                      <RowItem label="Complain" value={row.ServiceName} />
                      <RowItem label="Property" value={row.PropName} />
                      <RowItem label="Category" value={row.CategoryName} />
                      <RowItem
                        label="TktFor"
                        value={`${row.TktFor || "N/A"} | ${row.BedNo || "N/A"}`}
                        useTooltip={false}
                      />
                      {/* <RowItem
                        label="Priority"
                        value={row.TicketServName}
                        useTooltip={false}
                      /> */}{/* Priority + LeadTime in one row */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 0.5, flexWrap: "wrap" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                            Priority:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: "bold",
                              color: getPriorityColor(row.TicketServName),
                              fontSize: "0.9rem",
                              textShadow: "0 0 8px rgba(0,0,0,0.2)",
                            }}
                          >
                            {row.TicketServName || "N/A"}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                            Lead Time:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: row.LeadTime === "0.00 Min" ? "#52C41A" : "#1890ff",
                              fontSize: "0.85rem",
                            }}
                          >
                            {row.LeadTime || "N/A"}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Tag + DelayTime in one row with AI-style badge */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 0.5, flexWrap: "wrap" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                            Tag:
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: "0.85rem", color: "#722ed1" }}>
                            {row.TicketTagName || "N/A"}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                            Delay:
                          </Typography>
                          <Box
                            sx={{
                              px: 1.2,
                              py: 0.4,
                              borderRadius: "12px",
                              backgroundColor: getDelaySeverityColor(row.DelayTime),
                              color: "white",
                              fontWeight: "bold",
                              fontSize: "0.8rem",
                              minWidth: "70px",
                              textAlign: "center",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                            }}
                          >
                            {row.DelayTime || "On Time"}
                          </Box>
                        </Box>
                      </Box>
                      <RowItem label="Provider" value={row.ServiceProvider} />

                      {/* UPDATE BUTTON */}
                      <Box display="flex" justifyContent="flex-end" mt="auto" sx={{ marginX: { xs: 1, sm: 0 } }}>
                        {/* {row.TicketSt !== "O" && ( */}
                        <Tooltip title={row.TicketSt === "O" ? "Resolved and Closed tickets cannot be updated" : "Update ticket"} arrow>
                          {/* <Tooltip title={"Update ticket"} arrow></Tooltip> */}
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            // disabled={row.TicketSt === "C" || row.TicketSt === "R"}
                            onClick={(e) => {
                              e.stopPropagation();

                              // CASE 1: Multiple tickets already selected via checkboxes → Bulk Update
                              if (selectedRows.length >= 2) {
                                handleUpdate();
                                return;
                              }

                              // CASE 2: Exactly 1 ticket selected via checkbox → Single Update
                              if (selectedRows.length === 1) {
                                handleUpdate();
                                return;
                              }

                              // CASE 3: No checkbox selected → Treat this as "update this single ticket
                              setSelectedRows([row.TicketId]);
                              setTimeout(() => {
                                handleRowClick(row);
                              }, 0);
                            }}
                            sx={{
                              borderRadius: "12px",
                              textTransform: "none",
                              fontWeight: "bold",
                              fontSize: { xs: "0.99rem", sm: "0.85rem" },
                              height: { xs: "28px", sm: "30px" },
                              minWidth: { xs: "60px", sm: "70px" },
                              padding: { xs: "4px 8px", sm: "4px 10px" },
                            }}
                          >
                            Update
                          </Button>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </StyledCard>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box
            sx={{
              height: `${dynamicHeight}px`,
              minHeight: { xs: 200, sm: 300 },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: "1px dashed #ccc",
              borderRadius: "12px",
              mt: 2,
            }}
          >
            <Typography variant="body1" color="text.secondary">
              No Records Found
            </Typography>
          </Box>
        )}
      </Box>

      {/* ---------- FULL-SCREEN ZOOM DIALOG (with Close icon) ---------- */}
      <Dialog
        open={!!zoomedTicketId && !!zoomedImageUrl}
        onClose={() => {
          setZoomedTicketId(null);
          setZoomedImageUrl(null);
        }}
        maxWidth="lg"
        fullWidth
        BackdropProps={{ sx: { bgcolor: 'rgba(0,0,0,0.95)' } }}
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'hidden',
            m: { xs: 1, sm: 2 },
          },
        }}
      >
        {/* Close button */}
        <MuiIconButton
          onClick={() => {
            setZoomedTicketId(null);
            setZoomedImageUrl(null);
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(255,255,255,0.9)',
            color: 'black',
            '&:hover': { bgcolor: '#fff' },
            zIndex: 10,
            boxShadow: 3,
          }}
        >
          <CloseIcon />
        </MuiIconButton>

        {/* Click anywhere to close */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '90vh',
            cursor: 'zoom-out',
          }}
          onClick={() => {
            setZoomedTicketId(null);
            setZoomedImageUrl(null);
          }}
        >
          <Box
            component="img"
            src={zoomedImageUrl}
            alt="Zoomed ticket"
            // No onError → broken images show blank
            sx={{
              maxWidth: { xs: '95%', sm: '85%', md: '80%' },
              maxHeight: '90%',
              objectFit: 'contain',
              borderRadius: 2,
              boxShadow: 10,
              userSelect: 'none',
            }}
          />
        </Box>
      </Dialog>

      {/* TICKET DETAILS POPUP - SAME AS MultipleCards */}
      <TicketDetailsPopup
        open={detailPopupOpen}
        onClose={() => setDetailPopupOpen(false)}
        ticket={selectedTicketDetail}
      />
    </Box >
  );
};

export default TaskStatusCards;